import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Search, ChefHat, Clock, Users, Leaf, Heart, Loader2, Calendar, ChevronRight, Filter, Utensils, Apple, Flame, Droplet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

import { API } from '../config/api';

const CUISINES = ['Italian', 'Mexican', 'Chinese', 'Indian', 'American', 'Mediterranean', 'Japanese', 'Thai', 'French', 'Korean'];
const DIETS = ['Vegetarian', 'Vegan', 'Ketogenic', 'Paleo', 'Gluten Free', 'Dairy Free', 'Low FODMAP'];

export default function MealPlanner({ childId, childName, targetCalories = 2000 }) {
  const [view, setView] = useState('search'); // search, plan, recipe
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCuisine, setSelectedCuisine] = useState('');
  const [selectedDiet, setSelectedDiet] = useState('');
  const [maxTime, setMaxTime] = useState('');
  const [mealPlan, setMealPlan] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [apiConfigured, setApiConfigured] = useState(null);

  // Check if API is configured
  useEffect(() => {
    const checkApi = async () => {
      try {
        const res = await axios.get(`${API}/spoonacular/status`);
        setApiConfigured(res.data.configured);
      } catch {
        setApiConfigured(false);
      }
    };
    checkApi();
  }, []);

  const searchRecipes = useCallback(async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ query: searchQuery, number: 12 });
      if (selectedCuisine) params.append('cuisine', selectedCuisine.toLowerCase());
      if (selectedDiet) params.append('diet', selectedDiet.toLowerCase().replace(' ', ''));
      if (maxTime) params.append('max_ready_time', maxTime);
      
      const res = await axios.get(`${API}/spoonacular/recipes/search?${params}`);
      setSearchResults(res.data.recipes || []);
      
      if (res.data.recipes?.length === 0) {
        toast.info('No recipes found. Try different search terms.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to search recipes');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCuisine, selectedDiet, maxTime]);

  const generateMealPlan = useCallback(async (timeFrame = 'day') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        target_calories: targetCalories, 
        time_frame: timeFrame 
      });
      if (selectedDiet) params.append('diet', selectedDiet.toLowerCase().replace(' ', ''));
      
      const res = await axios.get(`${API}/spoonacular/meal-plan/generate?${params}`);
      setMealPlan({ ...res.data, timeFrame });
      setView('plan');
      toast.success(`${timeFrame === 'day' ? 'Daily' : 'Weekly'} meal plan generated!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate meal plan');
    } finally {
      setLoading(false);
    }
  }, [targetCalories, selectedDiet]);

  const viewRecipeDetails = useCallback(async (recipeId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/spoonacular/recipes/${recipeId}`);
      setSelectedRecipe(res.data);
      setView('recipe');
    } catch (err) {
      toast.error('Failed to load recipe details');
    } finally {
      setLoading(false);
    }
  }, []);

  if (apiConfigured === false) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
        <ChefHat className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-white mb-2">Meal Planner Coming Soon</h3>
        <p className="text-sm text-zinc-400">Spoonacular API key needs to be configured to enable this feature.</p>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="meal-planner">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
              <ChefHat className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Meal Planner</h3>
              <p className="text-xs text-zinc-400">Discover recipes & plan meals</p>
            </div>
          </div>
          
          {/* View Tabs */}
          <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-1">
            {[
              { id: 'search', label: 'Search', icon: Search },
              { id: 'plan', label: 'Meal Plan', icon: Calendar }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-all ${
                  view === tab.id || (view === 'recipe' && tab.id === 'search')
                    ? 'bg-green-500 text-white' 
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <tab.icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search View */}
      {view === 'search' && (
        <div className="p-4">
          {/* Search Bar */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchRecipes()}
                placeholder="Search recipes (e.g., chicken pasta, healthy smoothie)"
                className="pl-10 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <Button 
              onClick={searchRecipes}
              disabled={loading}
              className="bg-green-500 hover:bg-green-600"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <select
              value={selectedCuisine}
              onChange={(e) => setSelectedCuisine(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white"
            >
              <option value="">All Cuisines</option>
              {CUISINES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            
            <select
              value={selectedDiet}
              onChange={(e) => setSelectedDiet(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white"
            >
              <option value="">Any Diet</option>
              {DIETS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            
            <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5">
              <Clock className="h-4 w-4 text-zinc-400" />
              <input
                type="number"
                value={maxTime}
                onChange={(e) => setMaxTime(e.target.value)}
                placeholder="Max mins"
                className="w-20 bg-transparent text-sm text-white outline-none"
              />
            </div>
          </div>
          
          {/* Quick Meal Plan Buttons */}
          <div className="flex gap-2 mb-4 p-3 bg-zinc-800/30 rounded-lg border border-zinc-700">
            <span className="text-xs text-zinc-400 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Generate:
            </span>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => generateMealPlan('day')}
              disabled={loading}
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            >
              Daily Plan
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => generateMealPlan('week')}
              disabled={loading}
              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            >
              Weekly Plan
            </Button>
          </div>

          {/* Results */}
          {searchResults.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.map(recipe => (
                <div 
                  key={recipe.id}
                  onClick={() => viewRecipeDetails(recipe.id)}
                  className="bg-zinc-800/50 rounded-xl overflow-hidden border border-zinc-700 hover:border-green-500/50 transition-all cursor-pointer group"
                >
                  {recipe.image && (
                    <div className="relative h-32 overflow-hidden">
                      <img src={recipe.image} alt={recipe.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute bottom-2 left-2 flex gap-1">
                        {recipe.vegetarian && <span className="text-[10px] bg-green-500/80 text-white px-1.5 py-0.5 rounded">Vegetarian</span>}
                        {recipe.vegan && <span className="text-[10px] bg-emerald-500/80 text-white px-1.5 py-0.5 rounded">Vegan</span>}
                        {recipe.gluten_free && <span className="text-[10px] bg-yellow-500/80 text-white px-1.5 py-0.5 rounded">GF</span>}
                      </div>
                    </div>
                  )}
                  <div className="p-3">
                    <h4 className="font-semibold text-white text-sm line-clamp-2 mb-2 group-hover:text-green-400 transition-colors">
                      {recipe.title}
                    </h4>
                    <div className="flex items-center justify-between text-xs text-zinc-400">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {recipe.ready_in_minutes}m
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {recipe.servings}
                        </span>
                      </div>
                      <span className="text-green-400 font-medium">{Math.round(recipe.nutrition?.calories || 0)} cal</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {searchResults.length === 0 && !loading && (
            <div className="text-center py-8">
              <Utensils className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">Search for recipes or generate a meal plan</p>
            </div>
          )}
        </div>
      )}

      {/* Meal Plan View */}
      {view === 'plan' && mealPlan && (
        <div className="p-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setView('search')}
            className="mb-4 text-zinc-400"
          >
            ← Back to Search
          </Button>
          
          {mealPlan.timeFrame === 'day' ? (
            <>
              {/* Daily Plan */}
              <div className="mb-4 p-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/30">
                <h4 className="font-semibold text-white mb-2">Daily Nutrition Summary</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center">
                    <Flame className="h-4 w-4 mx-auto text-orange-400 mb-1" />
                    <div className="text-lg font-bold text-white">{Math.round(mealPlan.nutrients?.calories || 0)}</div>
                    <div className="text-[10px] text-zinc-400">Calories</div>
                  </div>
                  <div className="text-center">
                    <Apple className="h-4 w-4 mx-auto text-red-400 mb-1" />
                    <div className="text-lg font-bold text-white">{Math.round(mealPlan.nutrients?.protein || 0)}g</div>
                    <div className="text-[10px] text-zinc-400">Protein</div>
                  </div>
                  <div className="text-center">
                    <Droplet className="h-4 w-4 mx-auto text-yellow-400 mb-1" />
                    <div className="text-lg font-bold text-white">{Math.round(mealPlan.nutrients?.carbs || 0)}g</div>
                    <div className="text-[10px] text-zinc-400">Carbs</div>
                  </div>
                  <div className="text-center">
                    <Heart className="h-4 w-4 mx-auto text-pink-400 mb-1" />
                    <div className="text-lg font-bold text-white">{Math.round(mealPlan.nutrients?.fat || 0)}g</div>
                    <div className="text-[10px] text-zinc-400">Fat</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                {mealPlan.meals?.map((meal, idx) => (
                  <div 
                    key={meal.id}
                    onClick={() => viewRecipeDetails(meal.id)}
                    className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-green-500/50 cursor-pointer transition-all"
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={meal.image} alt={meal.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-green-400 font-medium mb-0.5">
                        {['Breakfast', 'Lunch', 'Dinner'][idx] || `Meal ${idx + 1}`}
                      </div>
                      <h4 className="font-medium text-white text-sm truncate">{meal.title}</h4>
                      <div className="flex items-center gap-2 text-xs text-zinc-400 mt-1">
                        <span><Clock className="h-3 w-3 inline mr-1" />{meal.ready_in_minutes}m</span>
                        <span><Users className="h-3 w-3 inline mr-1" />{meal.servings} servings</span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-zinc-500" />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Weekly Plan */}
              <div className="space-y-4">
                {mealPlan.days?.map((day) => (
                  <div key={day.day} className="bg-zinc-800/30 rounded-lg border border-zinc-700 overflow-hidden">
                    <div className="px-3 py-2 bg-zinc-800/50 border-b border-zinc-700 flex items-center justify-between">
                      <h4 className="font-semibold text-white">{day.day}</h4>
                      <span className="text-xs text-green-400">{Math.round(day.nutrients?.calories || 0)} cal</span>
                    </div>
                    <div className="p-3 flex gap-2 overflow-x-auto">
                      {day.meals?.map((meal) => (
                        <div 
                          key={meal.id}
                          onClick={() => viewRecipeDetails(meal.id)}
                          className="flex-shrink-0 w-32 bg-zinc-800/50 rounded-lg overflow-hidden border border-zinc-700 hover:border-green-500/50 cursor-pointer"
                        >
                          <img src={meal.image} alt={meal.title} className="w-full h-20 object-cover" />
                          <div className="p-2">
                            <p className="text-xs text-white line-clamp-2">{meal.title}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Recipe Detail View */}
      {view === 'recipe' && selectedRecipe && (
        <div className="p-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setView(mealPlan ? 'plan' : 'search')}
            className="mb-4 text-zinc-400"
          >
            ← Back
          </Button>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Image and Basic Info */}
            <div>
              {selectedRecipe.image && (
                <img src={selectedRecipe.image} alt={selectedRecipe.title} className="w-full h-48 object-cover rounded-xl mb-4" />
              )}
              <h3 className="text-xl font-bold text-white mb-2">{selectedRecipe.title}</h3>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedRecipe.vegetarian && <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Vegetarian</span>}
                {selectedRecipe.vegan && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">Vegan</span>}
                {selectedRecipe.gluten_free && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">Gluten Free</span>}
                {selectedRecipe.dairy_free && <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">Dairy Free</span>}
                {selectedRecipe.very_healthy && <span className="text-xs bg-pink-500/20 text-pink-400 px-2 py-1 rounded">Very Healthy</span>}
              </div>
              
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                  <Clock className="h-5 w-5 mx-auto text-green-400 mb-1" />
                  <div className="text-lg font-bold text-white">{selectedRecipe.ready_in_minutes}</div>
                  <div className="text-xs text-zinc-400">minutes</div>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                  <Users className="h-5 w-5 mx-auto text-blue-400 mb-1" />
                  <div className="text-lg font-bold text-white">{selectedRecipe.servings}</div>
                  <div className="text-xs text-zinc-400">servings</div>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                  <Heart className="h-5 w-5 mx-auto text-pink-400 mb-1" />
                  <div className="text-lg font-bold text-white">{selectedRecipe.health_score}</div>
                  <div className="text-xs text-zinc-400">health score</div>
                </div>
              </div>
            </div>
            
            {/* Nutrition */}
            <div>
              <h4 className="font-semibold text-white mb-3">Nutrition per Serving</h4>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {[
                  { label: 'Calories', value: selectedRecipe.nutrition?.calories, unit: '', color: 'orange' },
                  { label: 'Protein', value: selectedRecipe.nutrition?.protein, unit: 'g', color: 'red' },
                  { label: 'Carbs', value: selectedRecipe.nutrition?.carbs, unit: 'g', color: 'yellow' },
                  { label: 'Fat', value: selectedRecipe.nutrition?.fat, unit: 'g', color: 'pink' },
                  { label: 'Fiber', value: selectedRecipe.nutrition?.fiber, unit: 'g', color: 'green' },
                  { label: 'Sugar', value: selectedRecipe.nutrition?.sugar, unit: 'g', color: 'purple' }
                ].map(nutrient => (
                  <div key={nutrient.label} className="bg-zinc-800/50 rounded-lg p-2 flex items-center justify-between">
                    <span className="text-xs text-zinc-400">{nutrient.label}</span>
                    <span className={`text-sm font-bold text-${nutrient.color}-400`}>
                      {Math.round(nutrient.value || 0)}{nutrient.unit}
                    </span>
                  </div>
                ))}
              </div>
              
              <h4 className="font-semibold text-white mb-3">Ingredients</h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {selectedRecipe.ingredients?.map((ing, idx) => (
                  <div key={idx} className="text-sm text-zinc-300 py-1 border-b border-zinc-800">
                    {ing.original}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Instructions */}
          {selectedRecipe.instructions && (
            <div className="mt-4">
              <h4 className="font-semibold text-white mb-3">Instructions</h4>
              <div 
                className="text-sm text-zinc-300 prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedRecipe.instructions }}
              />
            </div>
          )}
        </div>
      )}
      
      {loading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-green-500 animate-spin" />
        </div>
      )}
    </div>
  );
}
